import { useEffect, useState } from 'react';
import type { LiveState } from '../hooks/useLive.js';
import '../styles/overlay.css';
import { formatClock, formatOddsValue } from '../api.js';
import { useCountdown, useLive } from '../hooks/useLive.js';
import { Logo } from '../components/Logo.js';

/**
 * OBS browser source.
 *
 * Rules this obeys, because it is composited over live gameplay:
 *   - never paints an opaque background
 *   - shows at most four markets, because it is glanced at, not read
 *   - animates only opacity and transform, so it cannot cost her frames
 *   - disappears entirely when there is no game, instead of showing an empty box
 *   - goes quiet rather than showing stale numbers if the feed drops
 *
 * Add in OBS as a Browser Source at 1920x1080, URL <site>/overlay.
 */
export function Overlay() {
  const live = useLive();
  const { book } = live;
  const countdown = useCountdown(book.lockInSeconds);
  const [stale, setStale] = useState(false);

  // If the socket has been down for a while, stop asserting prices are current.
  useEffect(() => {
    if (live.connected) {
      setStale(false);
      return;
    }
    const timer = setTimeout(() => setStale(true), 8_000);
    return () => clearTimeout(timer);
  }, [live.connected]);

  // While anything is bettable — the pregame board, or an in-play
  // micro-market — show those prices. Between micro-markets, switch to the
  // biggest stakes riding on the game. The card never goes dark mid-game.
  const open = book.markets.filter((m) => m.state === 'open');
  const topBets = (book.topBets ?? []).slice(0, 4);
  const inPlay = open.length === 0 && topBets.length > 0;
  const showing = open.slice(0, 4);
  const visible = book.live && (showing.length > 0 || topBets.length > 0) && !stale;

  return (
    <div className={`ov ${visible ? 'in' : 'out'}`}>
      <div className={`ov-card ${inPlay ? 'ov-closed' : ''}`}>
        <div className="ov-head">
          <span className="ov-brand">
            <Logo size={15} />
            <span className="ov-title">{inPlay ? 'BIGGEST BETS' : 'HIENBET'}</span>
          </span>
          {countdown !== null && countdown > 0 ? (
            <span className={`ov-lock ${countdown <= 30 ? 'urgent' : ''}`}>
              {formatClock(countdown)}
            </span>
          ) : (
            <span className="ov-lock closed">{inPlay ? 'IN PLAY' : 'OPEN'}</span>
          )}
        </div>

        <div className="ov-rows">
          {showing.length > 0
            ? showing.map((market) => (
                <div className="ov-row" key={market.id}>
                  <span className="ov-market">
                    {market.label}
                    {market.line !== null && <em> {market.line}</em>}
                  </span>
                  <span className="ov-prices">
                    {market.selections.map((selection) => (
                      <span className="ov-price" key={selection.id}>
                        <span className="ov-side">{selection.label}</span>
                        <span className="ov-odds">{formatOddsValue(selection.milliOdds)}</span>
                      </span>
                    ))}
                  </span>
                </div>
              ))
            : topBets.map((bet, i) => (
                <div className="ov-row" key={`${bet.displayName}-${i}`}>
                  <span className="ov-market">
                    <span className="ov-bettor">{bet.displayName}</span> {bet.marketLabel}
                  </span>
                  <span className="ov-odds">{bet.stake.toLocaleString()}</span>
                </div>
              ))}
        </div>

        <div className="ov-foot">play chips · no cash value</div>
      </div>

      <TickerStrip live={live} />
    </div>
  );
}

/**
 * Big bets and big wins, one at a time.
 *
 * A scrolling marquee is unreadable at stream compression and impossible to
 * catch mid-game, so entries are shown individually and cycled. Only stakes
 * above the configured threshold ever get here, so anything that appears is
 * worth her reacting to.
 */
/** Colour tokens duplicated from PlayerName — the overlay ships standalone CSS. */
const TICKER_COLORS: Record<string, string> = {
  frost: '#86c9ff',
  jade: '#35d68a',
  crimson: '#e2564f',
  violet: '#a97bff',
  ember: '#f0913c',
  mono: '#f2f6fa',
};

function TickerStrip({ live }: { live: LiveState }) {
  // A rolling stack, not a rotation: the newest three entries sit on screen
  // together, each new one sliding in on top and pushing the oldest off. The
  // stack visibly grows and changes as action comes in.
  const [, forceRefresh] = useState(0);

  // Entries age out at 90s; nothing else triggers a render when the feed goes
  // quiet, so tick occasionally to let stale rows drop off.
  useEffect(() => {
    const timer = setInterval(() => forceRefresh((n) => n + 1), 10_000);
    return () => clearInterval(timer);
  }, []);

  const entries = live.ticker.filter((e) => Date.now() - e.at < 90_000).slice(0, 3);
  if (entries.length === 0) return null;

  return (
    <div className="ov-tickers">
      {entries.map((entry) => {
        const effect = entry.style?.effect;
        const nameColor =
          !effect && entry.style?.color ? TICKER_COLORS[entry.style.color] : undefined;
        const signature = entry.style?.signature === true;

        return (
          <div
            className={`ov-ticker ${entry.kind} ${signature ? 'ov-signature' : ''}`}
            key={entry.id}
          >
            {entry.style?.egg && (
              <span className="ov-egg">
                <Logo size={11} />
              </span>
            )}
            <span
              className={`ov-ticker-who ${effect ? `ov-fx ov-fx-${effect}` : ''}`}
              style={nameColor ? { color: nameColor } : undefined}
            >
              {entry.displayName}
            </span>
            {entry.kind === 'payout' ? (
              <>
                <span className="ov-ticker-verb">won</span>
                <span className="ov-ticker-amount">{entry.amount.toLocaleString()}</span>
                <span className="ov-ticker-market">{entry.marketLabel}</span>
              </>
            ) : (
              <>
                <span className="ov-ticker-verb">bet</span>
                <span className="ov-ticker-amount">{entry.amount.toLocaleString()}</span>
                <span className="ov-ticker-market">{entry.marketLabel}</span>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}
